import React, { useEffect, useState, useCallback, useContext } from 'react'
import { useSelector } from 'react-redux';
import styles from './ModuleNewRecord.module.css'
import { useTranslation } from "react-i18next";
import ModuleSingleRecordSections from './ModuleSingleRecordSections';
import CrmButton from '../../../UI/Crm/CrmButton/CrmButton';
import CrmDivider from '../../../UI/Crm/CrmLayout/CrmDivider';
import { Box } from '@mui/material';
import { insertBaseRecord, linkRelatedRecords } from '../../../../axios/requests/baseRecordReq'
import AlertContext from '../../../../context/AlertModelContext/AlertModalContext';
import { useSearchParams } from 'react-router-dom';
import { translateServerApiErrors } from '../../../../util/functions';
import { consolidateCascadingDropdowns } from './CascadingDropdownUtils';


const ModuleNewRecord = ({ onCancelClicked, onNewRecordAdded, moduleObj, relatedToRecordId, onReloadGrid, relatedModuleObj, onSelectConv, OnAddToDialListClicked, onSaveStarted }) => {
    const alertCtx = useContext(AlertContext)
    const [moduleSections, setModuleSections] = useState([])
    const [searchParams] = useSearchParams();
    const [fieldValues, setFieldValues] = useState(() => {
        const initialValues = {};
        for (const [key, value] of searchParams.entries()) {
            let processedValue = value;

            // Convert 972 Israeli phone numbers to local format
            if (processedValue.trim().startsWith('972')) {
                processedValue = '0' + processedValue.trim().slice(3);
            }

            initialValues[key] = processedValue;
        }
        return initialValues;
    });
    const [pendingLinks, setPendingLinks] = useState([]); // [{ fieldName, record }]
    const [t] = useTranslation('crm')
    const crmState = useSelector(state => state.crm)
    let selectedCrmModule
    if (moduleObj) {
        selectedCrmModule = moduleObj
    }
    else {
        selectedCrmModule = crmState?.selectedCrmModule
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFieldValues((prev) => ({
            ...prev,
            [name]: newValue
        }));

        // Optimized live validation: only update the specific field
        setModuleSections((prevSections) => {
            return prevSections.map(section => {
                const updatedFields = section.fields?.map(field => {
                    if (field.name === name && field.required) {
                        if (newValue) {
                            return { ...field, errorMsg: '' }; // clear error only for that field
                        }
                    }
                    return field;
                }) || [];

                return {
                    ...section,
                    fields: updatedFields
                };
            });
        });
    };

    // Loading of module sections and setting default values
    useEffect(() => {
        if (selectedCrmModule?.sections?.length) {
            const orderedSection = JSON.parse(JSON.stringify(selectedCrmModule.sections))
                .filter((section) => section.order && section.name !== 'systemSection')
                .sort((sectionA, sectionB) => sectionA.order - sectionB.order)

            orderedSection.forEach(section => {
                const orderedSectionFields = [...section.fields].filter((field) => field.order).sort((fieldA, fieldB) => fieldA.order - fieldB.order)
                section.fields = orderedSectionFields
            })
            setModuleSections(orderedSection)

            // Set default values for fields that have defaultValue property
            const defaultValues = {};
            orderedSection.forEach(section => {
                section.fields?.forEach(field => {
                    if (field.defaultValue !== null && field.defaultValue !== undefined && field.defaultValue !== '') {
                        defaultValues[field.name] = field.defaultValue;
                    }
                });
            });

            // Update fieldValues with default values, but don't override existing values from URL params
            setFieldValues(prevFieldValues => {
                const updatedValues = { ...defaultValues, ...prevFieldValues };
                return updatedValues;
            });
        }
    }, [selectedCrmModule?.sections])

    const validateRequiredFields = (moduleSections, fieldValues) => {
        const updatedSections = JSON.parse(JSON.stringify(moduleSections));
        const missingFields = [];

        updatedSections.forEach(section => {
            if (!section.fields) return;

            section.fields.forEach(field => {
                if (field.required) {
                    const value = fieldValues[field.name];
                    if (!value) {
                        field.errorMsg = t('This field is required');
                        missingFields.push(field.name);
                    } else {
                        field.errorMsg = '';
                    }
                }
            });
        });

        return {
            updatedSections,
            missingFields
        };
    };

    const fixAllFieldsValuesWithFullMongoObjToRefModelId = (bodyFields) => {
        const fixedFields = { ...bodyFields }
        const fieldsKeys = Object.keys(fixedFields)
        fieldsKeys.forEach(fieldKey => {
            if (fixedFields[fieldKey]?.moduleType && fixedFields[fieldKey]._id) {
                fixedFields[fieldKey] = fixedFields[fieldKey]._id
            }
        })
        return fixedFields
    }

    const handleSubmit = useCallback(async () => {
        try {
            const moduleId = selectedCrmModule?._id
            if (!moduleId) return console.log('Please select module.')

            // Validate before saving (do this BEFORE triggering loading state)
            const { updatedSections, missingFields } = validateRequiredFields(moduleSections, fieldValues);

            if (missingFields.length > 0) {
                console.log(`Missing required fields: ${missingFields.join(', ')}`);
                setModuleSections(updatedSections); // Update form to show errors
                return;
            }

            // Trigger loading state AFTER validation passes
            onSaveStarted?.()

            // Show loading spinner in alert modal
            alertCtx.showAlert('', <div style={{textAlign: 'center', padding: '20px'}}><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div>, 'sm')

            let bodyFields = JSON.parse(JSON.stringify(fieldValues))
            bodyFields = fixAllFieldsValuesWithFullMongoObjToRefModelId(bodyFields)
            // Consolidate cascading dropdown data before sending to backend
            const consolidatedFields = consolidateCascadingDropdowns(bodyFields, moduleSections)
            const insertedRecord = await insertBaseRecord(moduleId, consolidatedFields)
            if (insertedRecord) {
                const recordId = insertedRecord._id
                if (recordId && pendingLinks.length > 0) {
                    pendingLinks.forEach(({ record, moduleObj }) => {
                        linkRelatedRecords(recordId, record._id, selectedCrmModule._id, moduleObj._id)
                    });
                    setPendingLinks([]);
                }

                // Handle linking first, then reload grid and close modal
                if (relatedToRecordId && relatedModuleObj) {// It should be link to related module
                    await linkRelatedRecords(relatedToRecordId, insertedRecord._id, relatedModuleObj._id, insertedRecord.moduleId)
                }
                
                // Now reload the grid after both record creation and linking are complete
                onNewRecordAdded()
                onReloadGrid?.()
                onCancelClicked()
                alertCtx.showAlert(t('success'), t('Record has been added successfuly.'), 'sm')

            }
            else {
                alertCtx.showAlert(t('error'), t('Failed to add record. Please contact the system administrator.'), 'sm')
            }
        }
        catch (err) {
            let fieldErrors 
            const apiErrorMsg = err.response?.data?.message // extract message from api
            if (apiErrorMsg) fieldErrors = translateServerApiErrors(apiErrorMsg, t)
            else fieldErrors = t('Failed to add record. Please contact the system administrator.')
            alertCtx.showAlert(t('error'), fieldErrors, 'sm')
        }
    }, [fieldValues, selectedCrmModule, moduleSections, pendingLinks, onSaveStarted])

    const onLinkClickedHandler = useCallback((selectedRows, fieldName, moduleObj) => {
        setPendingLinks(prev => [
            ...prev.filter(item => item.fieldName !== fieldName), // ensure no duplicates
            { fieldName, moduleObj, record: selectedRows[0] } // assuming single-selection
        ]);
    }, [])

    const onUnLinkClickedHandler = useCallback((selectedRows, fieldName, moduleObj) => {
        setPendingLinks(prev => prev.filter(item => item.fieldName !== fieldName));
    }, [])

    return (
        <Box
            component="form"
            onSubmit={(e) => e.preventDefault()}
            noValidate
            sx={{
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 1,
                    pr: 2 // Optional: to prevent scroll bar from overlapping content
                }}
            >
                <ModuleSingleRecordSections
                    moduleSections={moduleSections}
                    onEditSectionField={() => { }}
                    fieldValues={fieldValues}
                    onFieldChange={handleChange}
                    alwaysOpenToEdit={true}
                    onLinkClicked={onLinkClickedHandler}
                    onUnLinkClicked={onUnLinkClickedHandler}
                    onSelectConv={onSelectConv}
                    OnAddToDialListClicked={OnAddToDialListClicked}
                    relatedModuleObj={relatedModuleObj}

                />
            </Box>
            <CrmDivider sx={{ my: 2 }} />

            {/* Footer Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} sx={{ pr: 2 }}>
                {/* <CrmButton variant="contained" color="primary" onClick={handleSubmit}>
                    {t('Save')}
                </CrmButton> */}
                <CrmButton color="crmContainedGray94" onClick={handleSubmit}>
                    {t('Save')}
                </CrmButton>
                {/* <CrmButton variant="outlined" color="primary" onClick={onCancelClicked}>
                    {t('Cancel')}
                </CrmButton> */}
                <CrmButton color="crmOutlinedGray94" onClick={onCancelClicked}>
                    {t('Cancel')}
                </CrmButton>
            </Box>
        </Box>
    )
}

export default ModuleNewRecord